import { type SortOrder } from 'antd/lib/table/interface';
import { max } from 'lodash';
import React, { useState } from 'react';

import { type TQueryFilter, type TServerDataTableContext } from 'components/DataTable/types';
import { EWorkContainerArchiveOptions } from 'horizon/routes/WorkOrders/Tasks/TaskList/types';
import { getItem, storageManager } from 'utils/storage';
import { type User } from 'utils/types';

// FIXME: Certain tables include different sub-records depending on the active parent record.
// We need more sophisticated storage management to persist pagination correctly for each parent.
// For now we can correctly persist pagination for generic tables in the list. - AN 8/20/24
export const genericClientTableStorageIds = [
  'alerts-table',
  'campaign-list-table',
  'unplanned-tasks-list-table',
  'upcoming-task-list',
];

export const rowKey = ({ key, id }: { key?: string; id?: string }): React.Key => {
  return key ?? id ?? '';
};

export function filtersHaveChanged(oldFilters: TQueryFilter[], newFilters: TQueryFilter[]) {
  return JSON.stringify(oldFilters) !== JSON.stringify(newFilters);
}

/**
 * True when the table has at least one active column filter.
 * AntD may keep empty arrays for inactive filters; those do not count.
 */
export const hasActiveTableFilters = (filteredInfo?: Record<string, unknown> | null): boolean =>
  Object.values(filteredInfo ?? {}).some(value =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );

export function useServerDataTableContextState(defaultState?: Partial<TServerDataTableContext>) {
  const [context, setContext] = useState<TServerDataTableContext>({
    loading: false,
    refetch: undefined,
    refetchAwait: undefined,
    tableFilters: [],
    ...defaultState,
  });

  return { context, setContext };
}

export function rePrioritzeRowsForFilteredSorting(currentData: any[], prioritizedItems: any[]) {
  // Find the list of updated items:
  const updatedRows = prioritizedItems.map(row => {
    return { id: row.id, priority: row.priority };
  });
  const updatedRowIds = updatedRows.map(row => row.id);
  const updatedRowPriorities = updatedRows.map(row => row.priority);

  const sorted = currentData
    .map((row: any) => {
      // If current row is part of updated rows use the next priority
      if (updatedRowIds.includes(row.id)) {
        const shiftedRowPriority = updatedRowPriorities.shift();
        return { ...row, newPriority: shiftedRowPriority };
      }

      return { ...row, newPriority: row.priority };
    })
    .sort((a, b) => a.newPriority - b.newPriority);

  return sorted;
}

/**
 * Helper that is meant to handle reprioritizing items for data collections that include archived items that have been filtered out from the current table contents.
 * To use this helper correctly, pass in both the entire collection of archived and unarchived rows, as well as the prioritized collection of unarchived rows.
 * @param allData All data rows to be sorted, including both archived and unarchived rows
 * @param prioritizedData Data to be sorted that only included unarchived rows
 * @returns A sorted array containing all archived and unarchived rows, with the archived rows sorted at the end of the array
 */
export const reprioritzeRowsThatIncludeArchived = (allData: any[], prioritizedData: any[]) => {
  // Grab all of the archived rows from the entire collection of data
  const archived = allData.filter(d => d.archived === true);
  // Assumming the prioritized data contains no archived rows, map through it and set the newPriority value according to index
  let unarchivedPriority = 1;
  const updatedPriorityUnarchived = prioritizedData.map(row => {
    const updated = { ...row, newPriority: unarchivedPriority };
    unarchivedPriority++;
    return updated;
  });
  // Find the maximum newPriority value to use as a starting place for the archived priority count
  const maxUpdatedPriority = max(updatedPriorityUnarchived.map(row => row.newPriority));
  let archivedPriority = maxUpdatedPriority + 1;
  // Map through the archived rows, set newPriority
  const updatedPriorityArchived = archived.map((archivedRow: any) => {
    const newRow = { ...archivedRow, newPriority: archivedPriority };
    archivedPriority++;
    return newRow;
  });
  // Sort by newPriority
  const sorted = [...updatedPriorityUnarchived, ...updatedPriorityArchived].sort(
    (a, b) => a.newPriority - b.newPriority
  );
  return sorted;
};

export const customTableRowClassName = (record: any, _index: number) => {
  // handle styling of Work Container/Tasks related tables:
  const { archived, __typename } = record;
  if (
    ['WorkCampaign', 'WorkOrder'].includes(__typename) ||
    record?.type?.__typename === 'WorkContainerType'
  ) {
    return archived ? EWorkContainerArchiveOptions.archived : '';
  }

  // handle styling of LoggingTable
  if (record?.isChildTaskLog || record?.isChildTaskAttachment) {
    return 'marked-table-row';
  }

  return '';
};

export const archivedRowClassName = (record: any, _index: number) => {
  const { archived, __typename } = record;
  if (!archived) {
    return '';
  }

  // Keep our styling to only Work Container/Tasks related tables:
  if (
    ['WorkCampaign', 'WorkOrder'].includes(__typename) ||
    record?.type?.__typename === 'WorkContainerType'
  ) {
    return archived ? EWorkContainerArchiveOptions.archived : '';
  }
  return '';
};

export const tableStorage = ({ storageKey, user }: { storageKey?: string; user?: User }) => {
  const customerId = getItem('customerId');
  if (storageKey && customerId) {
    const key = [storageKey, customerId, user?.id].filter(Boolean).join('::');
    return storageManager({ storageKey: key });
  }

  return storageManager({ storageKey: undefined });
};

export const getMultiFilterValueForKey = (
  filters: { key: string; value: any[] }[],
  key: string
) => {
  return filters.find(f => f.key === key)?.value;
};

export const descSortConfig = { sortDirections: ['descend', 'ascend'] as SortOrder[] };

/** Heuristic widths aligned with antd5 table header layout (14px font, 16px horizontal padding). */
export const COLUMN_WIDTH_CONSTANTS_V5 = {
  HORIZONTAL_PADDING: 32,
  CHAR: 9,
  SORTER: 20,
  FILTER: 32,
  ICON: 20,
  MIN: 150,
} as const;

/** Heuristic widths aligned with antd v4 table header layout (12px font, 12px horizontal padding). */
export const COLUMN_WIDTH_CONSTANTS_V4 = {
  HORIZONTAL_PADDING: 24,
  CHAR: 10,
  SORTER: 16,
  FILTER: 24,
  ICON: 16,
  MIN: 150,
} as const;

export type AntdTableVersion = 'v4' | 'v5';

export type ColumnWidthConstants =
  | typeof COLUMN_WIDTH_CONSTANTS_V4
  | typeof COLUMN_WIDTH_CONSTANTS_V5;

/** @deprecated Use COLUMN_WIDTH_CONSTANTS_V5 or getColumnWidthConstants() */
export const COLUMN_WIDTH_CONSTANTS = COLUMN_WIDTH_CONSTANTS_V5;

/**
 * Returns the column-width heuristic constants for the given Ant Design table version.
 *
 * Use `'v5'` for client tables (`ClientDataTable` / antd5) and `'v4'` for server tables
 * (`ServerDataTable` / antd v4).
 *
 * @param version - Ant Design table version. Defaults to `'v5'`.
 * @returns Width constants for title text, padding, and header affordances (sorter, filter, icon).
 */
export const getColumnWidthConstants = (version: AntdTableVersion = 'v5'): ColumnWidthConstants =>
  version === 'v4' ? COLUMN_WIDTH_CONSTANTS_V4 : COLUMN_WIDTH_CONSTANTS_V5;

/**
 * Returns whether a React element `type` is a custom component rather than an intrinsic DOM tag.
 *
 * Used when estimating column title width for icons and tooltips rendered as function components
 * or styled-components.
 *
 * @param type - The `type` field from a React element (e.g. `'span'`, a component, or styled component).
 * @returns `true` for function components and styled-components; otherwise `false`.
 */
export const isReactComponentType = (type: unknown): boolean => {
  if (typeof type === 'function') {
    return true;
  }

  if (type != null && typeof type === 'object') {
    return 'styledComponentId' in type || '__emotion_base' in type;
  }

  return false;
};

/**
 * Extracts plain-text and extra icon width from a column title React node.
 *
 * Recursively walks strings, numbers, arrays, and elements to build the label text used by
 * {@link getWidthOfColumn}. Custom components and styled-components contribute extra width for
 * header icons/tooltips.
 *
 * @param node - Column title (`column.title`), which may be a string or React node.
 * @param version - Ant Design table version used to resolve icon width constants. Defaults to `'v5'`.
 * @returns Plain-text title and additional pixel width for icon/tooltip affordances.
 */
export const getTitleInfo = (
  node: React.ReactNode,
  version: AntdTableVersion = 'v5'
): { text: string; extraWidth: number } => {
  const { ICON } = getColumnWidthConstants(version);

  if (node == null || typeof node === 'boolean') {
    return { text: '', extraWidth: 0 };
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return { text: String(node), extraWidth: 0 };
  }

  if (Array.isArray(node)) {
    return node.reduce(
      (acc, child) => {
        const result = getTitleInfo(child, version);

        return {
          text: acc.text + result.text,
          extraWidth: acc.extraWidth + result.extraWidth,
        };
      },
      { text: '', extraWidth: 0 }
    );
  }

  if (React.isValidElement(node)) {
    const children = getTitleInfo(node.props.children, version);

    const componentExtra = isReactComponentType(node.type) ? ICON : 0;

    return {
      text: children.text,
      extraWidth: Math.max(children.extraWidth, componentExtra),
    };
  }

  return { text: '', extraWidth: 0 };
};

export const isHiddenColumn = (column: { className?: string }) =>
  Boolean(column.className?.includes('hidden-column'));

/**
 * Applies filters from hidden columns to table data before it is passed to the table component.
 *
 * Hidden columns (`className` includes `hidden-column`) are not rendered, but their
 * `filteredValue` and `onFilter` still need to constrain visible rows. A row is kept when it
 * matches at least one selected value for every active hidden filter column (AND across columns,
 * OR within a column's selected values — matching antd filter semantics).
 *
 * @typeParam T - Row shape in the table data source.
 * @param data - Rows to filter.
 * @param filterColumns - Column definitions that may include hidden filter columns.
 * @returns Rows that satisfy all active hidden-column filters, or the original `data` when none apply.
 */
export const applyHiddenColumnFilters = <T extends Record<string, unknown>>(
  data: T[],
  filterColumns?: Array<{
    className?: string;
    filteredValue?: unknown[] | null;
    onFilter?: (value: unknown, record: T) => boolean;
  }>
): T[] => {
  const hiddenFilterColumns =
    filterColumns?.filter(
      column => isHiddenColumn(column) && column.filteredValue?.length && column.onFilter
    ) ?? [];

  if (!hiddenFilterColumns.length) {
    return data;
  }

  return data.filter(row =>
    hiddenFilterColumns.every(column =>
      (column.filteredValue ?? []).some(value => column.onFilter!(value, row))
    )
  );
};

/**
 * Estimates the pixel width for a table column header.
 *
 * Explicit `column.width` values are returned as-is. Otherwise width is derived from the title
 * (via {@link getTitleInfo}), sorter/filter affordances, cell padding, and version-specific
 * constants from {@link getColumnWidthConstants}. Hidden columns return `undefined`.
 *
 * @param column - Ant Design column definition (`title`, `key`, `name`, `sorter`, `filterProperty`, etc.).
 * @param version - Ant Design table version. Defaults to `'v5'`.
 * @returns Column width in pixels, or `undefined` for hidden columns.
 */
export const getWidthOfColumn = (column: any, version: AntdTableVersion = 'v5') => {
  if (isHiddenColumn(column)) {
    return undefined;
  }

  if (column?.width != null) {
    return column.width;
  }

  const { text: titleText, extraWidth: iconsWidth } = getTitleInfo(column?.title, version);

  const text =
    titleText || (typeof column?.name === 'string' ? column.name : '') || String(column?.key ?? '');

  const isSorter = !!column?.sorter;

  const isFilter = !!(column?.filters || column?.filterDropdown || column?.filterProperty);

  const { CHAR, HORIZONTAL_PADDING, SORTER, FILTER, MIN } = getColumnWidthConstants(version);

  const contentWidth =
    text.length * CHAR +
    iconsWidth +
    (isSorter ? SORTER : 0) +
    (isFilter ? FILTER : 0) +
    HORIZONTAL_PADDING;

  return Math.max(contentWidth, MIN);
};

/**
 * Assigns a computed `width` to each column for fixed table layout.
 *
 * Falsy entries (`undefined`, `null`, `false`) are removed before mapping. Pass `'v4'` when
 * columns are rendered by {@link ServerDataTable}; omit or pass `'v5'` for
 * {@link ClientDataTable}.
 *
 * @typeParam T - Column definition object type.
 * @param columns - Column definitions, optionally including falsy placeholders to be filtered out.
 * @param version - Ant Design table version used by {@link getWidthOfColumn}. Defaults to `'v5'`.
 * @returns Columns with a `width` property set on each entry.
 */
export const mapColumnsWithWidth = <T extends object>(
  columns: Array<T | false | null | undefined>,
  version: AntdTableVersion = 'v5'
): T[] =>
  columns
    .filter((column): column is T => Boolean(column))
    .map(column => ({
      ...column,
      width: getWidthOfColumn(column, version),
    }));
